import { inject, Injectable, signal } from "@angular/core";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { TelemetryService } from "../../../../../services/api/telemetry/telemetry.service";
import { DateTime, DateTimeUnit } from "luxon";
import { EnergyToolExtractionService } from "../../../../../services/state/energy-tool-extraction/energy-tool-extraction.service";
import { forkJoin } from "rxjs";
import { EnergyToolUtilsService } from "../../../../../services/utils/energy-tool-utils/energy-tool-utils.service";

import { DoughnutChartData } from "../../../../../models/ui/chartData";
import { BarChartData, LineChartData } from "../../../../../models/ui/energyToolCharts";
import { Building } from "../../../../../models/api/asset/building";
import { EnergyToolUpdateService } from "../../../../../services/state/energy-tool-update/energy-tool-update.service";
import { AssetService } from "../../../../../services/api/asset/asset.service";
import { ForecastEnergyLoss } from "../../../../../models/ui/weatherForecast";
import { isNotNullOrUndefined } from "../../../../../shared/utils/utils";
import { ForecastTelemetryEntry, TelemetryEntry } from "../../../../../models/shared/telemetry";
import {
  TimeseriesSubscriptionHandle,
  TimeseriesWebsocketService,
} from "../../../../../services/websocket/timeseries-websocket/timeseries-websocket.service";

@Injectable()
export class BuildingEnergyToolService {
  // State signals for Building Energy Tool UI:
  private readonly _barChartEnergyLoss = signal<BarChartData[] | null>(null);
  readonly barChartEnergyLoss = this._barChartEnergyLoss.asReadonly();

  private readonly _lineChartEnergyLoss = signal<LineChartData[] | null>(null);
  readonly lineChartEnergyLoss = this._lineChartEnergyLoss.asReadonly();

  private readonly _doughnutChartEnergyLoss = signal<DoughnutChartData[] | null>(null);
  readonly doughnutChartEnergyLoss = this._doughnutChartEnergyLoss.asReadonly();

  private readonly _energyLoss = signal<number | null>(null);
  readonly energyLoss = this._energyLoss.asReadonly();

  private readonly _energyCost = signal<number | null>(null);
  readonly energyCost = this._energyCost.asReadonly();

  private readonly _insideTemp = signal<TelemetryEntry | null | undefined>(null);
  readonly insideTemp = this._insideTemp.asReadonly();

  private readonly _outsideTemp = signal<TelemetryEntry | null | undefined>(null);
  readonly outsideTemp = this._outsideTemp.asReadonly();

  private readonly _forecastWeather = signal<ForecastTelemetryEntry | null | undefined>(null);
  readonly forecastWeather = this._forecastWeather.asReadonly();

  private readonly _forecastEnergyLoss = signal<ForecastEnergyLoss | null | undefined>(null);
  readonly forecastEnergyLoss = this._forecastEnergyLoss.asReadonly();

  private readonly _expectedEnergyLossPerDay = signal<number | null>(null);
  readonly expectedEnergyLossPerDay = this._expectedEnergyLossPerDay.asReadonly();

  // Services needed for logic of this facade:
  private timeseriesWebsocket = inject(TimeseriesWebsocketService);
  private timeseriesSubscription?: TimeseriesSubscriptionHandle;
  private telemetryService = inject(TelemetryService);
  private assetService = inject(AssetService);

  private energyExtraction = inject(EnergyToolExtractionService);
  private energyUpdate = inject(EnergyToolUpdateService);
  private energyUtils = inject(EnergyToolUtilsService);

  /**
   * Resets all state signals to null.
   */
  private resetStates() {
    this._barChartEnergyLoss.set(null);
    this._lineChartEnergyLoss.set(null);
    this._energyLoss.set(null);
    this._expectedEnergyLossPerDay.set(null);
    this._doughnutChartEnergyLoss.set(null);
    this._insideTemp.set(null);
    this._outsideTemp.set(null);
    this._forecastWeather.set(null);
    this._forecastEnergyLoss.set(null);
    this._energyCost.set(null);
  }

  /**
   * Loads initial state of the Building Energy Tool.
   * @param buildingId - The ID of the Building asset.
   */
  loadInitialState(buildingId: string) {
    this.resetStates();
    const now = DateTime.now().toMillis();
    const startOfMonth = DateTime.now().startOf("month").toMillis();

    // Preparing all needed observables:
    const energyLoss$ = this.fetchBuildingEnergyLoss(buildingId, startOfMonth, now);

    const childrenEnergyLoss$ = this.fetchChildrenEnergyLoss(buildingId, startOfMonth, now);

    const buildingAsset$ = this.fetchBuildingAsset(buildingId);
    const buildingTelemtery$ = this.fetchBuildingTelemetry(buildingId);
    const forecast$ = this.telemetryService.getAttributes(buildingId, ASSET_TYPES.BUILDING, "forecast_weather_data");

    //Subscription:
    forkJoin({
      energyLoss: energyLoss$,
      childrenEnergyLoss: childrenEnergyLoss$,
      buildingAsset: buildingAsset$,
      buildingTelemetry: buildingTelemtery$,
      forecast: forecast$,
    }).subscribe({
      next: (res) => {
        const buildingEnergyLoss = res.energyLoss;
        const childrenEnergyLoss = res.childrenEnergyLoss as any[];
        const buildingAsset = res.buildingAsset;
        const buildingTelemetry = res.buildingTelemetry;
        const forecastData = res.forecast?.forecast_weather_data;

        this._forecastWeather.set(forecastData);

        if (
          isNotNullOrUndefined(buildingTelemetry.forecast_energy_loss_today?.[0]?.value) &&
          isNotNullOrUndefined(buildingTelemetry.forecast_energy_loss_tomorrow?.[0]?.value) &&
          isNotNullOrUndefined(buildingTelemetry.forecast_energy_loss_day_after_tomorrow?.[0]?.value)
        ) {
          this._forecastEnergyLoss.set({
            today: buildingTelemetry.forecast_energy_loss_today[0].value,
            tomorrow: buildingTelemetry.forecast_energy_loss_tomorrow[0].value,
            dayAfterTomorrow: buildingTelemetry.forecast_energy_loss_day_after_tomorrow[0].value,
          });
        }

        this.energyExtraction.extractAndSetBarChartData(
          childrenEnergyLoss,
          false,
          startOfMonth,
          "month",
          this._barChartEnergyLoss.set.bind(this._barChartEnergyLoss)
        );
        this.energyExtraction.extractAndSetLineChartData(
          buildingEnergyLoss?.energy_loss,
          buildingEnergyLoss?.normal_energy_loss,
          startOfMonth,
          "month",
          this._lineChartEnergyLoss.set.bind(this._lineChartEnergyLoss)
        );
        this.energyExtraction.extractAndSetTotalEnergyLoss(
          buildingEnergyLoss,
          "today",
          this._energyLoss.set.bind(this._energyLoss)
        );
        this.energyExtraction.extractAndSetEnergyCost(
          buildingEnergyLoss,
          "today",
          this._energyCost.set.bind(this._energyCost)
        );
        this.energyExtraction.extractAndSetExpectedEnergyLossPerDay(
          buildingAsset as Building,
          this._expectedEnergyLossPerDay.set.bind(this._expectedEnergyLossPerDay)
        );

        this.energyExtraction.extractAndSetTemp(
          buildingTelemetry,
          "Display_Sensor_Temp",
          this._insideTemp.set.bind(this._insideTemp)
        );

        this.energyExtraction.extractAndSetTemp(
          buildingTelemetry,
          "outside_temperature",
          this._outsideTemp.set.bind(this._outsideTemp)
        );

        this.energyExtraction.extractAndSetDoughnutChart(
          childrenEnergyLoss,
          this._doughnutChartEnergyLoss.set.bind(this._doughnutChartEnergyLoss)
        );
        this.initRealtime(buildingId);
      },
    });
  }

  private initRealtime(buildingId: string) {
    const descriptor = {
      entityType: ASSET_TYPES.BUILDING,
      entityId: [buildingId],
      keys: ["Display_Sensor_Temp", "outside_temperature"],
    };
    if (this.timeseriesSubscription) {
      this.timeseriesSubscription.replace(descriptor);
      return;
    }
    const subscription = this.timeseriesWebsocket.register(descriptor);
    this.timeseriesSubscription = subscription;
    subscription.messages$.subscribe({
      next: (incomingData) => {
        if (incomingData.data) {
          const insideTemperature = incomingData.data.Display_Sensor_Temp;
          const outsideTemperature = incomingData.data.outside_temperature;
          if (isNotNullOrUndefined(insideTemperature)) {
            this.energyExtraction.extractAndSetTemp(
              incomingData.data,
              "Display_Sensor_Temp",
              this._insideTemp.set.bind(this._insideTemp)
            );
          }
          if (isNotNullOrUndefined(outsideTemperature)) {
            this.energyExtraction.extractAndSetTemp(
              incomingData.data,
              "outside_temperature",
              this._outsideTemp.set.bind(this._outsideTemp)
            );
          }
        }
      },
      error: () => undefined,
    });
  }

  /**
   * Updates energy loss for given time window.
   * @param buildingId - The ID of the Building asset.
   * @param timeWindow - The time window to update energy loss for.
   */
  public updateEnergyLossForTimeWindow(buildingId: string, timeWindow: string) {
    this.energyUpdate.updateEnergyLossForTimeWindow(
      buildingId,
      timeWindow,
      this._energyLoss.set.bind(this._energyLoss),
      this._energyCost.set.bind(this._energyCost),
      this.fetchBuildingEnergyLoss.bind(this)
    );
  }

  /**
   * Updates bar chart data for given time range.
   * @param start - The start of the time range.
   * @param end - The end of the time range.
   * @param timeFrame - The time frame to update bar chart data for.
   * @param buildingId - The ID of the Building asset.
   * @param assetLabel - The label of the asset to update bar chart data for.
   */
  public updateBarChartForTimeRange(
    start: number,
    end: number,
    timeFrame: DateTimeUnit,
    buildingId: string,
    assetLabel: string | undefined
  ) {
    this.energyUpdate.updateBarChartForTimeWindow(
      buildingId,
      assetLabel,
      start,
      end,
      timeFrame,
      false,
      false,
      this._barChartEnergyLoss.set.bind(this._barChartEnergyLoss),
      this.fetchChildrenEnergyLoss.bind(this)
    );
  }
  /**
   * Updates doughnut chart data for given time range.
   * @param buildingId - The ID of the Building asset.
   * @param timeFrame - The time frame to update doughnut chart data for.
   */
  public updateDoughnutChartForTimeRange(buildingId: string, timeFrame: string) {
    this.energyUpdate.updateDoughnutChartForTimeWindow(
      buildingId,
      timeFrame,
      this._doughnutChartEnergyLoss.set.bind(this._doughnutChartEnergyLoss),
      this.fetchChildrenEnergyLoss.bind(this)
    );
  }

  /**
   * Updates line chart data for given time range.
   * @param start - The start of the time range.
   * @param end - The end of the time range.
   * @param timeFrame - The time frame to update line chart data for.
   * @param buildingId - The ID of the Building asset.
   */
  public updateLineChartForTimeRange(start: number, end: number, timeFrame: DateTimeUnit, buildingId: string) {
    this.energyUpdate.updateLineChartForTimeWindow(
      buildingId,
      start,
      end,
      timeFrame,
      this._lineChartEnergyLoss.set.bind(this._lineChartEnergyLoss),
      this.fetchBuildingEnergyLoss.bind(this)
    );
  }

  /**
   * Fetches energy loss data for given building, start and end times.
   * @param buildingId - The ID of the Building asset.
   * @param start - The start of the time range.
   * @param end - The end of the time range.
   * @returns An observable of energy loss data for given building, start and end times.
   */
  private fetchChildrenEnergyLoss(buildingId: string, start: number, end: number) {
    return this.energyUtils.getChildrenEnergyLossObservable(buildingId, ASSET_TYPES.BUILDING, start, end);
  }

  /**
   * Fetches energy loss data for given building, start and end times.
   * @param buildingId - The ID of the Building asset.
   * @param start - The start of the time range.
   * @param end - The end of the time range.
   * @returns An observable of energy loss data for given building, start and end times.
   */
  private fetchBuildingEnergyLoss(buildingId: string, start: number, end: number) {
    return this.telemetryService.getTimeseries(
      buildingId,
      ASSET_TYPES.BUILDING,
      "energy_loss,normal_energy_loss,energy_loss_costs",
      start,
      end
    );
  }

  /**
   * Fetches the asset details for the given building ID.
   * @param buildingId - The ID of the Building asset.
   * @returns An observable of the asset details.
   */
  private fetchBuildingAsset(buildingId: string) {
    return this.assetService.getAssetDetails(buildingId, ASSET_TYPES.BUILDING);
  }

  private fetchBuildingTelemetry(buildingId: string) {
    return this.telemetryService.getTimeseries(
      buildingId,
      ASSET_TYPES.BUILDING,
      "forecast_weather_data,outside_temperature,Display_Sensor_Temp,forecast_energy_loss_today,forecast_energy_loss_tomorrow,forecast_energy_loss_day_after_tomorrow"
    );
  }
}
